import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";

import { cn } from "@/lib/utils";

import type { MetricasSetter, NevoraDataProvider } from "../datos/dataProvider";
import { useResponsables } from "../datos/useResponsables";
import { euros } from "../dominio/comisiones";
import { pasoMasFlojo, rangoMes, sumarMetricas, tasa } from "../dominio/equipo";

/** Los pasos del embudo del setter, en orden. */
const PASOS_EMBUDO = [
  "llamadas",
  "citas",
  "consultorias",
  "propuestas",
  "cierres",
  "cobros",
] as const;

/**
 * 40 (Robert, 23/10/2026): el embudo de cada setter de un vistazo. Una barra por paso (ancho sobre
 * las llamadas), el % que pasa del paso anterior y, en rojo, el paso donde más se cae respecto al
 * equipo (lo que conviene entrenar). Con más de un setter, primero el del equipo entero.
 */
export const EmbudoSetters = ({ metricas }: { metricas: MetricasSetter[] }) => {
  const translate = useTranslate();
  const { nombre } = useResponsables();
  const equipo = sumarMetricas(metricas);
  return (
    <div
      className="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
      data-testid="embudo-visual"
    >
      {metricas.length > 1 ? (
        <TarjetaEmbudo
          titulo={translate("nevora.equipo.todo_el_equipo")}
          m={equipo}
          destacada
        />
      ) : null}
      {metricas.map((m) => (
        <TarjetaEmbudo
          key={String(m.sale_id)}
          titulo={nombre(m.sale_id)}
          m={m}
          equipo={metricas.length > 1 ? equipo : undefined}
        />
      ))}
    </div>
  );
};

const TarjetaEmbudo = ({
  titulo,
  m,
  equipo,
  destacada = false,
}: {
  titulo: string;
  m: Omit<MetricasSetter, "sale_id">;
  equipo?: Omit<MetricasSetter, "sale_id">;
  destacada?: boolean;
}) => {
  const translate = useTranslate();
  const base = Math.max(1, Number(m.llamadas));
  const flojo = equipo ? pasoMasFlojo(m, equipo, PASOS_EMBUDO) : null;
  const noShows = m.no_shows != null ? Number(m.no_shows) : null;
  return (
    <section
      className={cn(
        "rounded-lg border bg-card p-4 space-y-2",
        destacada && "border-[#3D8BD4]/60",
      )}
      data-testid="tarjeta-embudo"
    >
      <h3 className="font-medium">{titulo}</h3>
      <ul className="space-y-1.5">
        {PASOS_EMBUDO.map((p, i) => {
          const valor = Number(m[p]);
          const t = i > 0 ? tasa(Number(m[PASOS_EMBUDO[i - 1]]), valor) : null;
          const rojo = flojo === p;
          return (
            <li
              key={p}
              className="grid grid-cols-[6.5rem_1fr_auto] items-center gap-2 text-sm"
            >
              <span
                className={cn(
                  "truncate",
                  rojo && "font-medium text-rose-700 dark:text-rose-300",
                )}
              >
                {translate(`nevora.equipo.m.${p}`)}
              </span>
              <span className="h-2 rounded-full bg-muted">
                <span
                  className={cn(
                    "block h-2 rounded-full",
                    rojo ? "bg-rose-500" : "bg-[#3D8BD4]",
                  )}
                  style={{ width: `${Math.min(100, (valor / base) * 100)}%` }}
                />
              </span>
              <span className="tabular-nums text-right whitespace-nowrap">
                <span className="font-medium">{valor}</span>
                {t != null ? (
                  <span
                    className={cn(
                      "ml-1 text-xs",
                      rojo
                        ? "text-rose-700 dark:text-rose-300"
                        : "text-muted-foreground",
                    )}
                  >
                    {t} %
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground">
        {noShows != null
          ? `${translate("nevora.equipo.m.no_shows")}: ${noShows}${
              tasa(Number(m.citas), noShows) != null
                ? ` (${translate("nevora.equipo.de_las_citas", {
                    pct: tasa(Number(m.citas), noShows),
                  })})`
                : ""
            } · `
          : ""}
        {translate("nevora.equipo.m.comision")}: {euros(Number(m.comision))}
      </p>
      {flojo ? (
        <p className="text-xs font-medium text-rose-700 dark:text-rose-300">
          {translate("nevora.equipo.paso_flojo", {
            paso: translate(`nevora.equipo.m.${flojo}`),
          })}
        </p>
      ) : null}
    </section>
  );
};

/**
 * 40: quien hace consultorías (consultores, y Jan y Robert cuando están marcados): consultorías
 * agendadas, hechas, «No se presentó», cierres que salen de ellas y su comisión de consultor.
 * Informativo: el consultor no vende directamente.
 */
export const EmbudoConsultores = ({ mes }: { mes: string }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { nombre } = useResponsables();
  const { data, isPending } = useQuery({
    queryKey: ["nevora", "metricasConsultores", mes],
    queryFn: () => {
      const r = rangoMes(mes);
      return dataProvider.metricasConsultores(r.desde, r.hasta);
    },
  });
  const filas = (data ?? []).filter(
    (f) =>
      f.consultorias + f.hechas + f.no_shows + f.cierres > 0 ||
      Number(f.comision) > 0,
  );
  if (isPending) return null;
  if (!filas.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("nevora.equipo.sin_consultorias")}
      </p>
    );
  }
  return (
    <div
      className="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
      data-testid="embudo-consultores"
    >
      {filas.map((f) => {
        const cifras: { k: string; v: string; pct?: number | null }[] = [
          { k: "consultorias", v: String(f.consultorias) },
          {
            k: "hechas",
            v: String(f.hechas),
            pct: tasa(f.consultorias, f.hechas),
          },
          {
            k: "no_shows",
            v: String(f.no_shows),
            pct: tasa(f.consultorias, f.no_shows),
          },
          {
            k: "cierres",
            v: String(f.cierres),
            pct: tasa(f.hechas, f.cierres),
          },
        ];
        return (
          <section
            key={String(f.sale_id)}
            className="rounded-lg border bg-card p-4 space-y-2"
          >
            <h3 className="font-medium">{nombre(f.sale_id)}</h3>
            <dl className="grid grid-cols-4 gap-2">
              {cifras.map((c) => (
                <div key={c.k}>
                  <dt className="text-[11px] text-muted-foreground">
                    {translate(`nevora.equipo.c.${c.k}`)}
                  </dt>
                  <dd className="text-lg font-semibold tabular-nums">{c.v}</dd>
                  {c.pct != null ? (
                    <dd className="text-[11px] text-muted-foreground">
                      {c.pct} %
                    </dd>
                  ) : null}
                </div>
              ))}
            </dl>
            <p className="text-xs text-muted-foreground">
              {translate("nevora.equipo.c.comision")}:{" "}
              {euros(Number(f.comision))}
            </p>
          </section>
        );
      })}
    </div>
  );
};
